import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { Box, SxProps, Theme } from "@mui/material";
import { ReactElement, ReactNode } from "react";
import { FaIcon, FaIconProps } from "../Icon/FaIcon";

/**
 * What, if anything, is drawn around the dot and label.
 *
 * - `bare` (default) — nothing: no background, no border, no padding. Type and
 *   colour are inherited, so the same label reads as a heading inside a heading
 *   and as body text inside a row. Use it in running text, menu items and
 *   headings.
 * - `pill` — a rounded neutral container with its own background, padding and
 *   type scale. Only the dot stays caller-coloured. Use it when the label sits
 *   beside unrelated content and needs its own edge. The fill is translucent, so
 *   it keeps that edge on any backdrop — including one that moves under it, like
 *   a row that washes on hover.
 */
export const srStatusLabelVariants = ["bare", "pill"] as const;
export type SrStatusLabelVariant = (typeof srStatusLabelVariants)[number];

export const srStatusLabelSizes = ["small", "medium"] as const;
export type SrStatusLabelSize = (typeof srStatusLabelSizes)[number];

/**
 * Sizing is per-variant on purpose: `size` may not set type on `bare`, because
 * inheriting the container's type is what `bare` is for. On `bare` it therefore
 * moves the dot alone; on `pill` it moves type, padding and dot together.
 */
const SIZES = {
  small: { dotFontSize: "10px", padding: "3px 9px", typography: "caption" },
  medium: { dotFontSize: "12px", padding: "4px 10px", typography: "body2" },
} as const satisfies Record<SrStatusLabelSize, unknown>;

export type SrStatusLabelProps = Readonly<{
  /**
   * The dot's colour: a theme palette path (`"warning.light"`) or any CSS
   * colour. Required — a dot with no state to signal is just a bullet.
   */
  dotColor: string;
  /** Any node, so a caller may pass its own translated string or an element. */
  label?: ReactNode;
  /**
   * Render the dot alone. The label still needs an accessible equivalent, so
   * pass `aria-label` alongside it.
   */
  hideLabel?: boolean;
  variant?: SrStatusLabelVariant;
  /**
   * How large the whole thing is. On `pill` it moves type, padding and dot
   * together; on `bare` there is nothing to scale but the dot.
   */
  size?: SrStatusLabelSize;
  /** Space between dot and label. Defaults per variant. */
  gap?: number | string;
  /** Overrides the dot size `size` would give. */
  dotFontSize?: FaIconProps["fontSize"];
  /** Applied to the dot after its own styles. */
  dotSx?: SxProps<Theme>;
  /** Applied to the label after its own styles, including the ellipsis rules. */
  labelSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}>;

/**
 * **SrStatusLabel** shows one state as a coloured dot beside a label.
 *
 * The dot is the only thing that changes colour: the caller maps its own set of
 * states to colours and passes the result as `dotColor`, so no state vocabulary
 * lives here. The text ellipsises rather than wraps, because this sits on one
 * line beside other content.
 *
 * @example Bare, inside a heading — takes the heading's type and colour
 * ```tsx
 * <SrStatusLabel dotColor="warning.light" label="Pre-production" />
 * ```
 *
 * @example A pill, for a label sitting beside unrelated content
 * ```tsx
 * <SrStatusLabel variant="pill" dotColor="success.main" label="Approved" />
 * ```
 *
 * @example Dot only — the label still needs an accessible equivalent
 * ```tsx
 * <SrStatusLabel dotColor="error.light" hideLabel aria-label="Client review" />
 * ```
 *
 * @see SrChip — the interactive tag. The Storybook docs page for this component
 * covers when each one is the right choice.
 */
export const SrStatusLabel = ({
  dotColor,
  label,
  hideLabel = false,
  variant = "bare",
  size = "small",
  gap,
  dotFontSize,
  dotSx,
  labelSx,
  sx,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: SrStatusLabelProps): ReactElement => {
  const showLabel = !hideLabel && label != null;
  const isPill = variant === "pill";
  const sizing = SIZES[size];

  return (
    <Box
      component="span"
      className={className}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          gap: gap ?? (isPill ? 0.625 : 1),
          maxWidth: "100%",
          minWidth: 0,
          color: "inherit",
          // FaIcon keeps a trailing gutter for icons in list rows; the flex gap owns that spacing here.
          "& .MuiSvgIcon-root": {
            marginRight: 0,
          },
        },
        isPill &&
          ((theme: Theme) => ({
            borderRadius: `${theme.shape.borderRadiusMax}px`,
            padding: sizing.padding,
            typography: sizing.typography,
            color: theme.vars.palette.on.surface,
            // Translucent, so the lift is relative to the actual backdrop: an opaque step is
            // swallowed by any surface already one step along, such as a hovered row. Being
            // directional per theme, it also saves each mode needing its own value.
            backgroundColor: theme.vars.palette.surface.overlay,
          })),
        ...(Array.isArray(sx) ? sx : [sx].filter(Boolean)),
      ]}
    >
      <FaIcon
        data-testid={dataTestId && `${dataTestId}-dot`}
        icon={faCircle}
        fontSize={dotFontSize}
        automaticWidth
        sx={[
          {
            flex: "0 0 auto",
            color: dotColor,
            ...(dotFontSize ? {} : { fontSize: sizing.dotFontSize }),
          },
          ...(Array.isArray(dotSx) ? dotSx : [dotSx].filter(Boolean)),
        ]}
      />
      {showLabel ? (
        <Box
          component="span"
          className="SrStatusLabel-text"
          sx={[
            {
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            ...(Array.isArray(labelSx) ? labelSx : [labelSx].filter(Boolean)),
          ]}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  );
};
